// Gerenciador de Service Worker com detecção de atualizações
import { Capacitor } from "@capacitor/core";

type UpdateCallback = () => void;

let updateCallbacks: UpdateCallback[] = [];
let registration: ServiceWorkerRegistration | null = null;
let waitingWorker: ServiceWorker | null = null;

// Registra callback para quando houver atualização disponível
export function onUpdateAvailable(callback: UpdateCallback) {
  updateCallbacks.push(callback);
  
  // Se já tem um worker esperando, notifica imediatamente
  if (waitingWorker) {
    callback();
  }
  
  return () => {
    updateCallbacks = updateCallbacks.filter((cb) => cb !== callback);
  };
}

// Aplica a atualização (recarrega a página com nova versão)
export function applyUpdate() {
  if (waitingWorker) {
    // Diz ao SW para pular a espera e ativar
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  }
}

// Obtém a versão atual do SW
export async function getVersion(): Promise<string | null> {
  if (!registration?.active) return null;
  
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = (event) => {
      resolve(event.data.version);
    };
    registration!.active!.postMessage({ type: 'GET_VERSION' }, [channel.port2]);
  });
}

// Força limpeza de cache
export async function clearCache(): Promise<boolean> {
  if (!registration?.active) return false;
  
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = (event) => {
      resolve(event.data.success);
    };
    registration!.active!.postMessage({ type: 'CLEAR_CACHE' }, [channel.port2]);
  });
}

// Verifica por atualizações manualmente
export async function checkForUpdates(): Promise<boolean> {
  if (!registration) return false;
  
  try {
    await registration.update();
    return registration.waiting !== null;
  } catch (error) {
    console.error('[SW] Error checking for updates:', error);
    return false;
  }
}

// Registra o Service Worker
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || Capacitor.isNativePlatform()) {
    console.log('[SW] Service Workers not supported or running on Capacitor');
    return;
  }
  
  try {
    registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none', // Sempre busca o SW do servidor
    });
    
    console.log('[SW] Registered successfully');
    
    // Verifica se já tem uma atualização esperando
    if (registration.waiting) {
      waitingWorker = registration.waiting;
      notifyUpdateAvailable();
    }
    
    // Detecta quando um novo SW está instalado e esperando
    registration.addEventListener('updatefound', () => {
      const newWorker = registration!.installing;
      
      if (!newWorker) return;
      
      console.log('[SW] New version installing...');
      
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // Novo SW instalado, esperando para ativar
          console.log('[SW] New version ready to activate');
          waitingWorker = newWorker;
          notifyUpdateAvailable();
        }
      });
    });
    
    // Escuta mensagens do SW
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data.type === 'SW_ACTIVATED') {
        console.log('[SW] New version activated:', event.data.version);
        // Recarrega para usar a nova versão
        window.location.reload();
      }
    });
    
    // Detecta quando o SW controlador muda (após skipWaiting)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[SW] Controller changed, reloading...');
      window.location.reload();
    });
    
    // Verifica por atualizações a cada 5 minutos
    setInterval(() => {
      registration?.update();
    }, 5 * 60 * 1000);
    
    // Verifica por atualizações quando a tab volta ao foco
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        registration?.update();
      }
    });
    
  } catch (error) {
    console.error('[SW] Registration failed:', error);
  }
}

function notifyUpdateAvailable() {
  console.log('[SW] Update available, notifying listeners');
  updateCallbacks.forEach((callback) => callback());
}
